import React, { useState, useEffect } from 'react';
import { DeskLayout } from '../../components/DeskLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { getUsers, getProperties, assignProperties, getTemplates, createTemplate, resetTour, disableUser, enableUser, inviteUser } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { Users, FileText, Settings2, Plus, Building2, Shield, Loader2, PlayCircle, User, ChevronRight, Mail, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

export const Settings = () => {
  const { isAdmin, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [assignedProps, setAssignedProps] = useState([]);
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteData, setInviteData] = useState({
    name: '',
    email: '',
    role: 'OPERATOR',
    assignedProperties: [],
  });
  const [newTemplateData, setNewTemplateData] = useState({
    propertyId: '',
    type: 'INQUIRY_REPLY',
    title: '',
    body: '',
    tone: 'WARM'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [propsRes, templatesRes] = await Promise.all([
        getProperties(),
        getTemplates()
      ]);
      setProperties(propsRes.data);
      setTemplates(templatesRes.data);
      
      if (isAdmin) {
        const usersRes = await getUsers();
        setUsers(usersRes.data);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignProperties = async () => {
    if (!selectedUser) return;
    try {
      await assignProperties(selectedUser.id, assignedProps);
      await loadData();
      setSelectedUser(null);
      toast.success('Properties assigned');
    } catch (error) {
      toast.error('Failed to assign properties');
    }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplateData.title || !newTemplateData.body) {
      toast.error('Title and body are required');
      return;
    }
    try {
      await createTemplate(newTemplateData);
      setShowNewTemplate(false);
      setNewTemplateData({ propertyId: '', type: 'INQUIRY_REPLY', title: '', body: '', tone: 'WARM' });
      await loadData();
      toast.success('Template created');
    } catch (error) {
      toast.error('Failed to create template');
    }
  };

  const selectUserForAssignment = (u) => {
    setSelectedUser(u);
    setAssignedProps(u.assignedProperties || []);
  };

  const togglePropertyAssignment = (propId) => {
    setAssignedProps(prev => 
      prev.includes(propId) 
        ? prev.filter(id => id !== propId)
        : [...prev, propId]
    );
  };

  const toggleInviteProperty = (propId) => {
    setInviteData((prev) => ({
      ...prev,
      assignedProperties: prev.assignedProperties.includes(propId)
        ? prev.assignedProperties.filter((id) => id !== propId)
        : [...prev.assignedProperties, propId],
    }));
  };

  const handleInviteMember = async () => {
    const name = inviteData.name.trim();
    const email = inviteData.email.trim().toLowerCase();
    if (!name || !email) {
      toast.error('Name and email are required');
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setInviteSubmitting(true);
    try {
      const res = await inviteUser({
        name,
        email,
        role: inviteData.role,
        assignedProperties: inviteData.assignedProperties,
      });
      const dispatched = res.data?.email_dispatched;
      toast.success(
        dispatched
          ? `Invite sent to ${email}`
          : `User created — email delivery unavailable (${res.data?.delivery_reason || 'unknown'}). Use Forgot Password to share the setup link.`
      );
      setShowInvite(false);
      setInviteData({ name: '', email: '', role: 'OPERATOR', assignedProperties: [] });
      const usersRes = await getUsers();
      setUsers(usersRes.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to send invite');
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleToggleUserDisabled = async (u) => {
    const action = u.disabled ? 'enable' : 'disable';
    if (action === 'disable') {
      if (u.id === user?.id) {
        toast.error('You cannot disable your own account.');
        return;
      }
      if (!window.confirm(`Disable ${u.email}?\n\nThey will no longer be able to log in. Historical records remain. You can re-enable any time.`)) {
        return;
      }
    }
    try {
      if (action === 'disable') {
        await disableUser(u.id);
        toast.success(`${u.email} disabled`);
      } else {
        await enableUser(u.id);
        toast.success(`${u.email} enabled`);
      }
      const usersRes = await getUsers();
      setUsers(usersRes.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Couldn't ${action} user`);
    }
  };

  if (loading) {
    return (
      <DeskLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-96" />
        </div>
      </DeskLayout>
    );
  }

  return (
    <DeskLayout>
      <div className="space-y-6 animate-fade-in" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-medium text-stone-900">Settings</h1>
          <p className="text-stone-500 mt-1">Manage users, templates, and preferences</p>
        </div>

        <Tabs defaultValue="users" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="users"><Users className="h-4 w-4 mr-2" /> Users</TabsTrigger>
            <TabsTrigger value="templates"><FileText className="h-4 w-4 mr-2" /> Templates</TabsTrigger>
            <TabsTrigger value="preferences"><User className="h-4 w-4 mr-2" /> Preferences</TabsTrigger>
            <TabsTrigger value="integrations"><Settings2 className="h-4 w-4 mr-2" /> Integrations</TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <Card className="border-stone-200">
              <CardHeader className="pb-4 flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Users className="h-5 w-5" /> Team Members
                </CardTitle>
                {isAdmin && (
                  <Dialog open={showInvite} onOpenChange={setShowInvite}>
                    <DialogTrigger asChild>
                      <Button
                        className="bg-[#3F617E] hover:bg-[#345470] rounded-full"
                        data-testid="invite-member-button"
                      >
                        <UserPlus className="h-4 w-4 mr-1" /> Invite member
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md" data-testid="invite-member-dialog">
                      <DialogHeader>
                        <DialogTitle className="font-heading">Invite a team member</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <p className="text-[13px] text-stone-500 leading-relaxed">
                          We&apos;ll create their account and email them a one-time link to set their own password.
                          You will never see or set the password yourself.
                        </p>
                        <div className="space-y-2">
                          <Label>Name *</Label>
                          <Input
                            value={inviteData.name}
                            onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })}
                            placeholder="Alex Morgan"
                            data-testid="invite-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Email *</Label>
                          <Input
                            type="email"
                            value={inviteData.email}
                            onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                            placeholder="alex@property.com"
                            data-testid="invite-email-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Role</Label>
                          <Select
                            value={inviteData.role}
                            onValueChange={(v) => setInviteData({ ...inviteData, role: v })}
                          >
                            <SelectTrigger data-testid="invite-role-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="OPERATOR">Operator</SelectItem>
                              <SelectItem value="ADMIN">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-[11px] text-stone-400">
                            Operators handle day-to-day guest work · Admins manage settings and team.
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label>Property access</Label>
                          {properties.length === 0 ? (
                            <p className="text-[12px] text-stone-400">No properties yet — they&apos;ll get access once you create one.</p>
                          ) : (
                            <div className="max-h-44 overflow-y-auto rounded-md border border-stone-200 divide-y divide-stone-100">
                              {properties.map((prop) => {
                                const checked = inviteData.assignedProperties.includes(prop.id);
                                return (
                                  <button
                                    key={prop.id}
                                    type="button"
                                    onClick={() => toggleInviteProperty(prop.id)}
                                    className={`w-full text-left px-3 py-2 flex items-center gap-3 transition-colors ${
                                      checked ? 'bg-[#3F617E]/5' : 'hover:bg-stone-50'
                                    }`}
                                    data-testid={`invite-property-${prop.id}`}
                                  >
                                    <span
                                      className={`h-4 w-4 rounded border flex items-center justify-center ${
                                        checked ? 'bg-[#3F617E] border-[#3F617E]' : 'border-stone-300'
                                      }`}
                                      aria-hidden
                                    >
                                      {checked && <span className="block h-1.5 w-1.5 rounded-sm bg-white" />}
                                    </span>
                                    <span className="flex-1 min-w-0">
                                      <span className="block text-sm text-stone-800 truncate">{prop.name}</span>
                                      <span className="block text-[11px] text-stone-400 truncate">{prop.location || 'No location'}</span>
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                          <p className="text-[11px] text-stone-400">
                            Leave empty for access to all properties (legacy default for admins).
                          </p>
                        </div>
                        <Button
                          onClick={handleInviteMember}
                          disabled={inviteSubmitting}
                          className="w-full bg-[#3F617E] hover:bg-[#345470] rounded-full"
                          data-testid="invite-submit-button"
                        >
                          {inviteSubmitting ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending invite…</>
                          ) : (
                            <><Mail className="h-4 w-4 mr-2" /> Send invite</>
                          )}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </CardHeader>
              <CardContent>
                {isAdmin ? (
                  <>
                    {(() => {
                      // Team Members = ADMIN + OPERATOR only.
                      // DEMO is a sandbox showcase login, not a team member —
                      // it's surfaced inside the Showcase Workspace page.
                      const teamUsers = users.filter((u) => u.role !== 'DEMO');
                      const activeUsers = teamUsers.filter((u) => !u.disabled);
                      const disabledUsers = teamUsers.filter((u) => u.disabled);
                      const renderRow = (u, isDisabled) => (
                        <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                          <TableCell className={`font-medium ${isDisabled ? 'text-stone-400' : ''}`}>
                            {u.name}
                          </TableCell>
                          <TableCell className={isDisabled ? 'text-stone-400' : ''}>{u.email}</TableCell>
                          <TableCell>
                            <Badge
                              variant={u.role === 'ADMIN' ? 'default' : 'secondary'}
                              className={isDisabled ? 'opacity-60' : ''}
                            >
                              {u.role === 'ADMIN' ? <Shield className="h-3 w-3 mr-1" /> : null}
                              {u.role}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {isDisabled ? (
                              <Badge variant="outline" className="text-[10px] tracking-[0.18em] uppercase text-stone-400 border-stone-200">
                                Disabled
                              </Badge>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {u.assignedProperties?.slice(0, 2).map(propId => {
                                  const prop = properties.find(p => p.id === propId);
                                  return prop ? (
                                    <Badge key={propId} variant="outline" className="text-xs">{prop.name}</Badge>
                                  ) : null;
                                })}
                                {(u.assignedProperties?.length || 0) > 2 && (
                                  <Badge variant="outline" className="text-xs">+{u.assignedProperties.length - 2}</Badge>
                                )}
                                {(!u.assignedProperties || u.assignedProperties.length === 0) && (
                                  <span className="text-stone-400 text-sm">All properties</span>
                                )}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            {isDisabled ? (
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="rounded-full"
                                  onClick={() => handleToggleUserDisabled(u)}
                                  data-testid={`enable-user-${u.id}`}
                                >
                                  Re-enable
                                </Button>
                                <span className="text-[12px] text-stone-400">Cannot log in</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <Dialog>
                                <DialogTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="rounded-full"
                                    onClick={() => selectUserForAssignment(u)}
                                  >
                                    Assign
                                  </Button>
                                </DialogTrigger>
                                <DialogContent className="sm:max-w-md">
                                  <DialogHeader>
                                    <DialogTitle className="font-heading">Assign Properties to {u.name}</DialogTitle>
                                  </DialogHeader>
                                  <div className="space-y-4 py-4">
                                    <div className="space-y-2">
                                      {properties.map(prop => (
                                        <div
                                          key={prop.id}
                                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                                            assignedProps.includes(prop.id)
                                              ? 'border-[#3F617E] bg-[#3F617E]/5'
                                              : 'border-stone-200 hover:border-stone-300'
                                          }`}
                                          onClick={() => togglePropertyAssignment(prop.id)}
                                        >
                                          <div className="flex items-center gap-3">
                                            <Building2 className="h-5 w-5 text-stone-500" />
                                            <div>
                                              <p className="font-medium">{prop.name}</p>
                                              <p className="text-sm text-stone-500">{prop.location}</p>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                    <Button
                                      onClick={handleAssignProperties}
                                      className="w-full bg-[#3F617E] hover:bg-[#345470] rounded-full"
                                    >
                                      Save Assignments
                                    </Button>
                                  </div>
                                </DialogContent>
                              </Dialog>
                                {u.id !== user?.id && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="rounded-full text-stone-500 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => handleToggleUserDisabled(u)}
                                    data-testid={`disable-user-${u.id}`}
                                  >
                                    Disable
                                  </Button>
                                )}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      );

                      return (
                        <>
                          {/* Active users */}
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Role</TableHead>
                                <TableHead>Properties</TableHead>
                                <TableHead>Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {activeUsers.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={5} className="text-center text-stone-500 py-6">
                                    No active team members yet.
                                  </TableCell>
                                </TableRow>
                              ) : (
                                activeUsers.map((u) => renderRow(u, false))
                              )}
                            </TableBody>
                          </Table>

                          {/* Inactive users — collapsed by default, admin-only context */}
                          {disabledUsers.length > 0 && (
                            <details className="mt-6 group" data-testid="inactive-users-section">
                              <summary className="cursor-pointer select-none flex items-center gap-2 px-3 py-2 rounded-md hover:bg-stone-50 text-[13px] text-stone-600">
                                <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />
                                <span className="font-medium">Inactive users</span>
                                <Badge variant="outline" className="text-[10px] tracking-[0.18em] uppercase text-stone-500 border-stone-200">
                                  {disabledUsers.length}
                                </Badge>
                                <span className="text-stone-400">·</span>
                                <span className="text-stone-400 text-[12px]">retained for historical records, cannot log in</span>
                              </summary>
                              <div className="mt-3 rounded-md border border-stone-100 bg-stone-50/40">
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Name</TableHead>
                                      <TableHead>Email</TableHead>
                                      <TableHead>Role</TableHead>
                                      <TableHead>State</TableHead>
                                      <TableHead>Actions</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {disabledUsers.map((u) => renderRow(u, true))}
                                  </TableBody>
                                </Table>
                              </div>
                            </details>
                          )}
                        </>
                      );
                    })()}
                  </>
                ) : (
                  <div className="p-8 text-center">
                    <Shield className="h-12 w-12 text-stone-300 mx-auto mb-3" />
                    <p className="text-stone-500">Admin access required to manage users</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="templates">
            <Card className="border-stone-200">
              <CardHeader className="pb-4 flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <FileText className="h-5 w-5" /> Reply Templates
                </CardTitle>
                <Dialog open={showNewTemplate} onOpenChange={setShowNewTemplate}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#3F617E] hover:bg-[#345470] rounded-full" data-testid="add-template-button">
                      <Plus className="h-4 w-4 mr-1" /> Add Template
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="font-heading">Create Template</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Property (optional)</Label>
                        <Select value={newTemplateData.propertyId || 'global'} onValueChange={(v) => setNewTemplateData({...newTemplateData, propertyId: v === 'global' ? '' : v})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Global template (all properties)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="global">Global (All Properties)</SelectItem>
                            {properties.map(p => (
                              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Type</Label>
                          <Select value={newTemplateData.type} onValueChange={(v) => setNewTemplateData({...newTemplateData, type: v})}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="INQUIRY_REPLY">Inquiry Reply</SelectItem>
                              <SelectItem value="FOLLOWUP_1">Follow-up #1</SelectItem>
                              <SelectItem value="FOLLOWUP_2">Follow-up #2</SelectItem>
                              <SelectItem value="CLOSE_LOOP">Close-the-loop</SelectItem>
                              <SelectItem value="REVIEW_REPLY">Review Reply</SelectItem>
                              <SelectItem value="CHECKIN_MESSAGE">Check-in Message</SelectItem>
                              <SelectItem value="POSTSTAY_REVIEW_REQUEST">Post-stay Review Request</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Tone</Label>
                          <Select value={newTemplateData.tone} onValueChange={(v) => setNewTemplateData({...newTemplateData, tone: v})}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="WARM">Warm</SelectItem>
                              <SelectItem value="REFINED">Refined</SelectItem>
                              <SelectItem value="SHORT">Short</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Title</Label>
                        <Input 
                          value={newTemplateData.title} 
                          onChange={(e) => setNewTemplateData({...newTemplateData, title: e.target.value})}
                          placeholder="Template title"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Body</Label>
                        <Textarea 
                          value={newTemplateData.body} 
                          onChange={(e) => setNewTemplateData({...newTemplateData, body: e.target.value})}
                          placeholder="Template content... Use {name}, {dates}, {property} as placeholders"
                          rows={6}
                        />
                      </div>
                      <Button 
                        onClick={handleCreateTemplate}
                        className="w-full bg-[#3F617E] hover:bg-[#345470] rounded-full"
                      >
                        Create Template
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {templates.map((template) => (
                    <Card key={template.id} className="border-stone-200">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-medium text-stone-900">{template.title}</p>
                            <div className="flex gap-2 mt-1">
                              <Badge variant="secondary" className="text-xs">{template.type}</Badge>
                              <Badge variant="outline" className="text-xs">{template.tone}</Badge>
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-stone-600 mt-2 line-clamp-3">{template.body}</p>
                      </CardContent>
                    </Card>
                  ))}
                  {templates.length === 0 && (
                    <div className="col-span-2 text-center py-12 px-4">
                      <FileText className="h-12 w-12 text-stone-300 mx-auto mb-3" />
                      <p className="text-stone-700 font-medium">No templates created yet</p>
                      <p className="text-sm text-stone-500 mt-2 max-w-md mx-auto">
                        Create reusable replies for common guest inquiries — like availability questions, 
                        check-in instructions, or follow-up messages.
                      </p>
                      <p className="text-xs text-stone-400 mt-2">
                        Use placeholders like {'{name}'}, {'{dates}'}, {'{property}'} to personalize automatically.
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="preferences">
            <Card className="border-stone-200">
              <CardHeader className="pb-4">
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <User className="h-5 w-5" /> Your Preferences
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Account Info */}
                <div className="p-4 bg-stone-50 rounded-lg">
                  <p className="text-sm text-stone-500 mb-1">Logged in as</p>
                  <p className="font-medium text-stone-900">{user?.name}</p>
                  <p className="text-sm text-stone-600">{user?.email}</p>
                  <Badge variant="secondary" className="mt-2">{user?.role}</Badge>
                </div>

                {/* Tour */}
                <div className="p-4 border border-stone-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-stone-900">Product Tour</p>
                      <p className="text-sm text-stone-500">Learn how to use Maris Desk</p>
                    </div>
                    <Button
                      variant="outline"
                      className="rounded-full"
                      onClick={async () => {
                        try {
                          await resetTour();
                          // Update local storage
                          const storedUser = JSON.parse(localStorage.getItem('maris_user') || '{}');
                          storedUser.hasSeenTour = false;
                          localStorage.setItem('maris_user', JSON.stringify(storedUser));
                          toast.success('Tour reset. Refresh to see it again.');
                          // Reload page to trigger tour
                          window.location.reload();
                        } catch (error) {
                          toast.error('Failed to reset tour');
                        }
                      }}
                      data-testid="replay-tour-button"
                    >
                      <PlayCircle className="h-4 w-4 mr-2" />
                      Replay tour
                    </Button>
                  </div>
                </div>

                {/* Notification Preferences - Future */}
                <div className="p-4 border border-dashed border-stone-200 rounded-lg opacity-60">
                  <p className="font-medium text-stone-700">Notification Preferences</p>
                  <p className="text-sm text-stone-500 mt-1">Email and in-app notification settings coming soon.</p>
                </div>

                {/* Default Reply Tone - Future */}
                <div className="p-4 border border-dashed border-stone-200 rounded-lg opacity-60">
                  <p className="font-medium text-stone-700">Default Reply Tone</p>
                  <p className="text-sm text-stone-500 mt-1">Set your preferred AI draft tone (Warm, Refined, Short).</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="integrations">
            <Card className="border-stone-200">
              <CardHeader className="pb-4">
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Settings2 className="h-5 w-5" /> Integrations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-6 border border-dashed border-stone-300 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-stone-900">WhatsApp Business API</p>
                        <p className="text-sm text-stone-500">Connect your WhatsApp Business account</p>
                      </div>
                      <Badge variant="secondary">Coming in V2</Badge>
                    </div>
                  </div>
                  <div className="p-6 border border-dashed border-stone-300 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-stone-900">PMS Integration</p>
                        <p className="text-sm text-stone-500">Sync with your property management system</p>
                      </div>
                      <Badge variant="secondary">Coming in V3</Badge>
                    </div>
                  </div>
                  <div className="p-6 border border-dashed border-stone-300 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-stone-900">OTA Messaging</p>
                        <p className="text-sm text-stone-500">Connect Airbnb, Booking.com messaging</p>
                      </div>
                      <Badge variant="secondary">Coming in V3</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DeskLayout>
  );
};

export default Settings;
